import styled from 'styled-components';

import {
  colorAccent,
  colorAccentLighten100,
  colorBlack,
  colorBlack900,
  colorSecondary,
  colorWhite,
  fontFamilySecondary,
  mediaQueryDesktopMin,
  mediaQueryMobileLargeMin,
  mediaQueryTabletMin,
} from '@/styleUtils';

export const Wrapper = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    i {
      color: ${colorSecondary};
      font-style: normal;
    }
  }

  h1,
  h2,
  h3 {
    margin-bottom: 16px;

    @media ${mediaQueryMobileLargeMin} {
      margin-bottom: 24px;
    }
  }

  h4,
  h5,
  h6 {
    margin-bottom: 16px;
  }

  h1 {
    font-family: ${fontFamilySecondary};
    font-size: 18px;
    line-height: 22px;
    font-weight: 700;
    color: ${colorBlack900};

    @media ${mediaQueryTabletMin} {
      font-size: 24px;
      line-height: 28px;
    }

    @media ${mediaQueryDesktopMin} {
      font-size: 30px;
      line-height: 36px;
    }
  }

  h2 {
    font-family: ${fontFamilySecondary};
    font-size: 18px;
    line-height: 22px;
    font-weight: 700;
    color: ${colorBlack900};

    @media ${mediaQueryTabletMin} {
      font-size: 22px;
      line-height: 26px;
    }

    @media ${mediaQueryDesktopMin} {
      font-size: 24px;
      line-height: 28px;
    }
  }

  h3 {
    font-family: ${fontFamilySecondary};
    font-size: 16px;
    line-height: 20px;
    font-weight: 700;
    color: ${colorBlack900};

    @media ${mediaQueryTabletMin} {
      font-size: 18px;
      line-height: 22px;
    }

    @media ${mediaQueryDesktopMin} {
      font-size: 22px;
      line-height: 26px;
    }
  }

  h4 {
    font-family: ${fontFamilySecondary};
    font-size: 16px;
    line-height: 20px;
    margin: 0;
    font-weight: 700;
    color: ${colorBlack900};

    @media ${mediaQueryTabletMin} {
      font-size: 18px;
      line-height: 22px;
    }
  }

  h5 {
    font-family: ${fontFamilySecondary};
    font-size: 14px;
    line-height: 16px;
    font-weight: 700;
    color: ${colorBlack900};

    @media ${mediaQueryTabletMin} {
      font-size: 16px;
      line-height: 18px;
    }

    @media ${mediaQueryDesktopMin} {
      font-size: 16px;
      line-height: 20px;
    }
  }

  h6 {
    font-family: ${fontFamilySecondary};
    font-size: 14px;
    line-height: 18px;
    font-weight: 700;
    color: ${colorBlack900};
  }

  & .grid-3col-container {
    ul {
      width: 100%;
    }
    @media all and ${mediaQueryMobileLargeMin} {
      display: grid;
      gap: 8px;
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  & div.table-container {
    margin: 0 -16px 16px;
  }

  & div.table-container > div {
    overflow: auto;
  }

  & div.table-container > div > div {
    padding: 0 16px;
    min-width: 630px;
  }

  & table {
    border-collapse: collapse;
    border: 1px solid #e5e5e5;
    line-height: 20px;
    table-layout: unset;
    text-align: left;
    width: 100%;
    margin-bottom: 16px;

    @media ${mediaQueryMobileLargeMin} {
      margin-bottom: 24px;
    }

    thead {
      background-color: #f8fafb;
      font-weight: 700;
    }

    tr {
      width: 100%;
      &:not(:last-child) {
        border-bottom: 1px solid #e4eaf4;
      }

      td {
        padding: 16px;
        color: ${colorBlack900};
        min-width: 80px;
      }

      td:first-child {
        min-width: 40px;
      }

      th {
        padding: 16px;
        color: #575757;
        font-weight: normal;
      }
    }
  }

  & div.table-container table {
    margin-bottom: 0;
  }

  & hr {
    background: ${colorWhite};
    border: 0;
    border-top: 1px solid #e5e5e5;
    margin: 32px 0;
  }

  & > *:last-child {
    margin-bottom: 0;
  }

  & a {
    word-break: break-all;
    color: ${colorAccent};
  }

  & a.no-break {
    word-break: normal;
  }

  & p,
  & ul,
  & ol,
  & a {
    font-size: 14px;
    line-height: 24px;

    @media ${mediaQueryMobileLargeMin} {
      font-size: 16px;
      line-height: 28px;
    }
  }

  & p,
  & ul,
  & ol {
    margin-bottom: 16px;

    @media ${mediaQueryMobileLargeMin} {
      margin-bottom: 24px;
    }
  }

  & ul > li::before {
    content: '•';
    color: ${colorBlack900};
    margin-right: 8px;
  }

  & ul.dashed-list > li:before {
    content: '-';
    color: ${colorBlack900};
    margin-right: 2px;
  }

  & ol {
    counter-reset: my-counter;
    list-style: none;
    padding-left: 0;
  }

  & ol > li {
    margin: 0 0 0.5rem 0;
    counter-increment: my-counter;
    position: relative;
    color: ${colorBlack900};
  }

  & ol > li::before {
    content: counter(my-counter) '.';
    color: ${colorAccentLighten100};
    font-size: 16px;
  }

  & ol > li > p,
  & ul > li > p {
    display: inline;
    margin: 0;
  }

  & ol.normal-arabic-list {
    list-style-type: decimal;
  }

  & ol.normal-arabic-list > li {
    position: static;
  }

  & ol.normal-arabic-list > li:before {
    content: '';
    display: none;
  }

  & p:has(+ ul) {
    margin-bottom: 16px;
  }

  & p:has(iframe) {
    position: relative;
    padding-bottom: 56.25%;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 8px;
    }
  }

  & img {
    display: block;
    max-width: 100%;
  }

  & blockquote {
    font-size: 18px;
    line-height: 32px;
    padding: 32px;
    width: 100%;
    background-color: #fdf5f3;
    margin-bottom: 16px;
    color: ${colorBlack};
    border-radius: 2px;
    border-left: 4px solid ${colorSecondary};

    @media ${mediaQueryMobileLargeMin} {
      padding: 40px;
    }
  }

  & p.footer-text {
    text-align: center;
    font-size: 12px;
    line-height: 17px;
    color: #93a4ac;
    margin: 0;

    @media ${mediaQueryMobileLargeMin} {
      text-align: start;
    }
  }

  & div.iframe-container {
    position: relative;
    padding-bottom: 56.25%;
    width: 100%;

    iframe {
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      position: absolute;
    }
  }

  & p.list-item {
    display: flex;

    span:first-child {
      margin-right: 8px;
    }
  }
`;

export default Wrapper;
